import {Component, DestroyRef, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AppStateService} from '../../services/app-state.service';
import {BottomSheetService} from '../../services/bottom-sheet.service';
import {OrderService} from '../../services/order.service';
import {TranslateService} from '../../services/translate.service';
import {PrebookContext} from '../../models/Order';
import {ActionButtonComponent} from '../buttons/action-button.component';
import {PlainButtonComponent} from '../buttons/plain-button.component';

/**
 * How the party of a prebooking orders, for the booker: one orders for all, or everyone orders their own part
 * through a link the booker shares. Sharing takes a signed-in booker (only they may send the prebooking, and the
 * reminders go to their e-mail), so a guest still anonymous signs in first and the invite follows by itself.
 * Once shared, the sheet is where the link is shared again.
 */
@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, ActionButtonComponent, PlainButtonComponent],
  selector: 'app-prebook-invite-sheet',
  template: `
    <div class="max-w-lg mx-auto bg-theme-surface-80 pb-safe w-full px-6 py-6 text-theme-text">
      @if (!prebook?.invite_url) {
        <div class="text-theme-text2">{{ getString("Ni kan förbeställa på två sätt.") }}</div>
        <div class="flex flex-col gap-3 mt-4">
          <button class="text-left rounded-xl p-4 border-2 border-theme-divider bg-theme-surface" (click)="orderForAll()">
            <div class="text-lg font-bold">{{ getString("Jag beställer för alla") }}</div>
            <div class="text-theme-text2">{{ getString("Du väljer maten till hela sällskapet.") }}</div>
          </button>
          <button class="text-left rounded-xl p-4 border-2 bg-theme-surface"
                  [ngClass]="pending ? 'border-theme-secondarybutton' : 'border-theme-divider'" (click)="everyoneOrders()">
            <div class="text-lg font-bold">{{ getString("Alla beställer själva") }}</div>
            <div class="text-theme-text2">{{ getString("Du delar en länk med de andra, var och en väljer sin mat och du skickar in allt till slut.") }}</div>
          </button>
        </div>
        @if (pending && isAnon) {
          <div class="mt-4 text-theme-text2">{{ getString("Logga in för att bjuda in. Då är det bara du som kan skicka beställningen.") }}</div>
        }
        @if (askEmail) {
          <div class="mt-4">
            <div class="font-bold mb-1">{{ getString("Din e-post") }}</div>
            <div class="text-sm text-theme-text2 mb-2">{{ getString("Hit skickar vi besked när de andra har beställt.") }}</div>
            <input type="email" class="text-input" [(ngModel)]="email" placeholder="namn@exempel.se">
            <div class="mt-4">
              <app-action-button [text]="getString('Skapa länk')" [disabled]="!email.trim()" [spinner]="working" (onClick)="invite()"></app-action-button>
            </div>
          </div>
        }
      } @else {
        <div class="text-theme-text2">{{ getString("Dela länken med de andra i sällskapet. Var och en väljer sin mat, sedan skickar du beställningen.") }}</div>
        <div class="mt-4 rounded-xl bg-theme-surface2 p-3 text-sm break-all">{{ prebook?.invite_url }}</div>
        <div class="mt-4">
          <app-action-button [text]="getString('Dela länken')" icon="fa-share-nodes" [chevron]="false" (onClick)="share()"></app-action-button>
        </div>
        <div class="mt-3">
          <app-plain-button [text]="getString(copied ? 'Kopierad' : 'Kopiera länken')" textSize="text-lg" bgColor="bg-theme-surface2" [customClass]="'h-14'" (onClick)="copy()"></app-plain-button>
        </div>
      }
      @if (error) {
        <div class="mt-4 text-theme-notification2">{{ error }}</div>
      }
    </div>
  `
})
export class PrebookInviteSheetComponent implements OnInit {
  prebook?: PrebookContext;
  isAnon = true;
  // The booker picked "everyone orders", waiting for sign-in or their e-mail
  pending = false;
  askEmail = false;
  email = '';
  working = false;
  copied = false;
  error = '';
  private hasEmail = false;

  constructor(private appStateService: AppStateService, private sheetService: BottomSheetService,
              private orderService: OrderService, private translate: TranslateService, private destroyRef: DestroyRef) {}

  ngOnInit(): void {
    this.appStateService.observeAppState()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(appState => {
        this.prebook = appState.order?.prebook;
        this.isAnon = appState.order?.user_cfg?.is_anon ?? true;
        this.hasEmail = !!appState.order?.user_cfg?.email;
        // Back from signing in: the invite follows
        if (this.pending && !this.isAnon && !this.askEmail && !this.working && !this.prebook?.invite_url) {
          this.invite();
        }
      });
  }

  orderForAll() {
    this.sheetService.popSheet();
  }

  everyoneOrders() {
    this.pending = true;
    this.error = '';
    if (this.isAnon) {
      this.sheetService.displaySignin(1);
      return;
    }
    this.invite();
  }

  invite() {
    if (!this.hasEmail && !this.email.trim()) {
      this.askEmail = true;
      return;
    }
    this.working = true;
    this.orderService.prebookInvite(this.email.trim() || undefined).subscribe({
      next: () => {
        this.working = false;
        this.pending = false;
        this.askEmail = false;
      },
      error: (e) => {
        console.error("prebook_invite failed", e);
        this.working = false;
        this.error = this.getString("Länken kunde inte skapas. Försök igen.");
      }
    });
  }

  share() {
    const url = this.prebook?.invite_url;
    if (!url) {
      return;
    }
    if (navigator.share) {
      navigator.share({title: this.getString("Förbeställning"), text: this.getString("Välj din mat till vår bokning:"), url})
        .catch(() => {});
      return;
    }
    this.copy();
  }

  copy() {
    const url = this.prebook?.invite_url;
    if (url) {
      navigator.clipboard?.writeText(url).then(() => this.copied = true).catch(() => {});
    }
  }

  getString(s: string): string {
    return this.translate.getString(s);
  }
}
